<template>
  <div
    class="flex h-full flex-col gap-5 overflow-y-auto p-4 pt-20 xl:p-8 xl:pt-8"
  >
    <div
      class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
    >
      <div>
        <p class="text-sm font-medium text-eorange-600">{{ $t("admin.auto.consola_global") }}</p>
        <h1 class="text-2xl font-bold text-eblack-900">{{ $t("admin.auto.administradores") }}</h1>
        <p class="text-sm text-eblack-500">{{ $t("admin.auto.cree_administradores_globales_de_emanta") }}</p>
      </div>
      <button
        type="button"
        class="inline-flex items-center justify-center rounded-lg bg-egreen-500 px-4 py-2 text-sm font-medium text-white"
        @click="openForm = true"
      >{{ $t("admin.auto.nuevo_administrador") }}</button>
    </div>

    <form
      v-if="openForm"
      class="grid gap-3 rounded-lg border border-ewhite-200 bg-white p-4 md:grid-cols-2"
      @submit.prevent="createAdmin"
    >
      <input
        v-model.trim="form.dni"
        class="rounded-lg border-ewhite-300"
        :placeholder="$t('admin.auto.identificacion')"
        required
      />
      <input
        v-model.trim="form.assigned_person"
        class="rounded-lg border-ewhite-300"
        :placeholder="$t('admin.auto.nombre')"
        required
      />
      <input
        v-model.trim="form.email"
        type="email"
        class="rounded-lg border-ewhite-300"
        :placeholder="$t('admin.auto.correo')"
        required
      />
      <PhoneInput v-model="form.contact_phone" />
      <input
        v-model="form.password"
        type="password"
        class="rounded-lg border-ewhite-300"
        :placeholder="$t('admin.auto.contrasena_temporal')"
        required
      />
      <div class="flex gap-2">
        <button
          type="submit"
          class="rounded-lg bg-egreen-500 px-4 py-2 text-sm text-white"
        >{{ $t("admin.auto.crear") }}</button>
        <button
          type="button"
          class="rounded-lg border px-4 py-2 text-sm"
          @click="openForm = false"
        >{{ $t("admin.auto.cancelar") }}</button>
      </div>
    </form>

    <div class="grid gap-4 md:grid-cols-2">
      <article
        v-for="admin in platformAdmins"
        :key="admin.id"
        class="rounded-lg border border-ewhite-200 bg-white p-5"
      >
        <h2 class="font-semibold text-eblack-900">
          {{ admin.assigned_person }}
        </h2>
        <p class="text-sm text-eblack-500">{{ admin.email }}</p>
        <div class="mt-3 flex items-center justify-between gap-3">
          <span
            class="inline-flex rounded-full bg-egreen-100 px-2 py-1 text-xs text-egreen-700"
          >{{ $t("admin.auto.plataforma") }}</span>
          <button
            type="button"
            class="rounded-lg bg-eblack-700 px-3 py-1.5 text-xs text-white"
            @click="openPasswordReset(admin)"
          >{{ $t("admin.auto.restablecer_contrasena") }}</button>
        </div>
      </article>
    </div>

    <PasswordResetModal
      v-if="passwordResetUser"
      :title="$t('admin.auto.restablecer_contrasena')"
      :user-label="passwordResetUser.email"
      :help-text="$t('admin.auto.restablecer_contrasena_ayuda')"
      :new-password-label="$t('admin.auto.contrasena_nueva')"
      :confirm-password-label="$t('admin.auto.confirmar_contrasena')"
      :submit-text="$t('admin.auto.restablecer')"
      :submitting-text="$t('admin.auto.actualizando')"
      :cancel-text="$t('admin.auto.cancelar')"
      :min-length-text="$t('admin.auto.minimo_8_caracteres')"
      :mismatch-text="$t('admin.auto.las_contrasenas_no_coinciden')"
      :is-submitting="passwordResetLoading"
      :error="passwordResetError"
      @submit="resetPassword"
      @cancel="closePasswordReset"
    />
  </div>
</template>

<script setup>
import { i18n } from "@/i18n";
import { computed, onMounted, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import usersRequests from "@/services/admin/users-service";
import PhoneInput from "@/components/ui/PhoneInput.vue";
import PasswordResetModal from "@/components/ui/PasswordResetModal.vue";

const toast = useToast();
const users = ref([]);
const openForm = ref(false);
const passwordResetUser = ref(null);
const passwordResetLoading = ref(false);
const passwordResetError = ref("");
const form = reactive({
  dni: "",
  assigned_person: "",
  email: "",
  contact_phone: "",
  password: "",
});

const platformAdmins = computed(() =>
  users.value.filter((user) => user.is_super_admin === true)
);

async function load() {
  const response = await usersRequests.getAll();
  users.value = response.data ?? [];
}

async function createAdmin() {
  await usersRequests.savePlatformAdmin({ ...form });
  toast.success(i18n.global.t("admin.auto.administrador_creado"));
  Object.assign(form, {
    dni: "",
    assigned_person: "",
    email: "",
    contact_phone: "",
    password: "",
  });
  openForm.value = false;
  await load();
}

function openPasswordReset(user) {
  passwordResetUser.value = user;
  passwordResetError.value = "";
}

function closePasswordReset() {
  if (passwordResetLoading.value) return;
  passwordResetUser.value = null;
  passwordResetError.value = "";
}

async function resetPassword(newPassword) {
  if (!passwordResetUser.value) return;
  passwordResetLoading.value = true;
  passwordResetError.value = "";
  try {
    await usersRequests.resetPassword(passwordResetUser.value.id, { newPassword });
    toast.success(i18n.global.t("admin.auto.contrasena_actualizada"));
    closePasswordReset();
  } catch (error) {
    passwordResetError.value = i18n.global.t("admin.auto.error_actualizando_contrasena");
  } finally {
    passwordResetLoading.value = false;
  }
}

onMounted(load);
</script>
